import { chunk, isNil } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useCompanyScopeContext } from "@ryde/redux";
import {
  CrudApi,
  errors,
  GlobalAppEventEnum,
  IApiError,
  IDynamicEntity,
  IDynamicObject,
  Log,
  normalizeErrors,
  RydeUiUnit,
} from "@ryde/shared";

import { PROCESS_CHUNK_COUNT } from "../generic-upload.constants";
import {
  IGenericUploadFailedItem,
  IGenericUploadProcessStatistics,
} from "../generic-upload.models";

export const useGenericUploadProcess = (entity: RydeUiUnit) => {
  const { t } = useTranslation();
  const { eventEmitter } = useCompanyScopeContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStatistics, setProcessStatistics] =
    useState<IGenericUploadProcessStatistics>({
      totalToProcess: 0,
      successfullyCreated: 0,
      successfullyUpdated: 0,
      failed: 0,
    });
  const [processFailedItems, setProcessFailedItems] = useState<
    IGenericUploadFailedItem[]
  >([]);

  const processUploadData = useEvent(
    async (dtos: IDynamicObject[]): Promise<void> => {
      const dtosToCreate: IDynamicObject[] = [];
      const dtosToUpdate: IDynamicEntity[] = [];

      for (const dto of dtos) {
        if (isNil(dto.id)) {
          dtosToCreate.push(dto);
        } else {
          dtosToUpdate.push(dto as IDynamicEntity);
        }
      }

      const statistics: IGenericUploadProcessStatistics = {
        totalToProcess: dtosToCreate.length + dtosToUpdate.length,
        successfullyCreated: 0,
        successfullyUpdated: 0,
        failed: 0,
      };

      setIsProcessing(true);
      setProcessFailedItems([]);
      setProcessStatistics(statistics);

      const failedItems: IGenericUploadFailedItem[] = [];

      for (const dtosChunk of chunk(dtosToCreate, PROCESS_CHUNK_COUNT)) {
        for (const dto of dtosChunk) {
          try {
            await CrudApi.create(entity, dto);
            statistics.successfullyCreated += 1;
          } catch (error) {
            const errorMessage = normalizeErrors(error as IApiError, t);
            failedItems.push({
              dto,
              error: errorMessage.join(", ") || t(errors.common.unknownError),
              operation: "Create",
            });
            statistics.failed += 1;
          }
        }

        setProcessStatistics({ ...statistics });
        setProcessFailedItems([...failedItems]);
      }

      for (const dtosChunk of chunk(dtosToUpdate, PROCESS_CHUNK_COUNT)) {
        for (const dto of dtosChunk) {
          try {
            await CrudApi.update(entity, dto.id, dto);
            statistics.successfullyUpdated += 1;
          } catch (error) {
            const errorMessage = normalizeErrors(error as IApiError, t);
            failedItems.push({
              dto,
              error: errorMessage.join(", ") || t(errors.common.unknownError),
              operation: "Update",
            });
            statistics.failed += 1;
          }
        }

        setProcessStatistics({ ...statistics });
        setProcessFailedItems([...failedItems]);
      }

      setIsProcessing(false);
      eventEmitter?.emit(GlobalAppEventEnum.PROCESS_COMPLETED);

      Log.debug({
        msg: "Generic upload completed",
        totalToProcess: statistics.totalToProcess,
        createdCount: statistics.successfullyCreated,
        updatedCount: statistics.successfullyUpdated,
        failedCount: statistics.failed,
      });
      Log.debug({ msg: "Generic upload failed items", failedItems });
    },
  );

  return {
    isProcessing,
    processStatistics,
    processFailedItems,
    processUploadData,
  };
};
